import type { EmotionDetectorConfig, LocalClassifierConfig } from './types';
import { avgKeys, clamp } from './LocalEmotionUtils';
import type { EmotionScore } from './classifiers/service/Scorer';
import { scoreAllEmotions as scoreAllEmotionsWithScorer } from './classifiers/service/Scorer';
import {
  computeStabilityFromSamples as computeBaselineStability,
  finalizeCalibration as finalizeCalibrationWithMath,
  type CalibrationBaseline,
  type EyeContextSnapshot,
} from './classifiers/service/CalibrationMath';

const STABILITY_KEYS = [
  'browDownLeft', 'browDownRight', 'eyeSquintLeft', 'eyeSquintRight',
  'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft', 'mouthFrownRight',
  'jawOpen', 'eyeWideLeft', 'eyeWideRight', 'browInnerUp',
];

export class LocalEmotionClassifierService {
  finalizeCalibration(samples: Record<string, number>[]): { baseline: CalibrationBaseline; baselineStability: number } | null {
    return finalizeCalibrationWithMath(samples, STABILITY_KEYS);
  }

  isWithinNeutralRange(baseline: CalibrationBaseline | null, bs: Record<string, number>, config: Required<LocalClassifierConfig>): boolean {
    if (!baseline) return false;
    const sh = config.smileHysteresis;
    const fh = config.frownHysteresis;

    const smile = avgKeys(bs, ['mouthSmileLeft', 'mouthSmileRight']);
    const frown = avgKeys(bs, ['mouthFrownLeft', 'mouthFrownRight']);
    const browDown = avgKeys(bs, ['browDownLeft', 'browDownRight']);

    const eyeWide = avgKeys(bs, ['eyeWideLeft', 'eyeWideRight']);
    const jawOpen = avgKeys(bs, ['jawOpen']);

    const smileInRange = smile >= (baseline.smile.p10 - sh) && smile <= (baseline.smile.p90 + sh);
    const frownInRange = frown >= (baseline.frown.p10 - fh) && frown <= (baseline.frown.p90 + fh);
    const browDownInRange = browDown >= (baseline.browDown.p10 - 0.04) && browDown <= (baseline.browDown.p90 + 0.08);
    const eyeWideInRange = eyeWide >= (baseline.eyeWide.p10 - 0.04) && eyeWide <= (baseline.eyeWide.p90 + 0.06);
    const jawOpenInRange = jawOpen >= (baseline.jawOpen.p10 - 0.04) && jawOpen <= (baseline.jawOpen.p90 + 0.08);

    return smileInRange && frownInRange && browDownInRange && eyeWideInRange && jawOpenInRange;
  }

  isEmotionWeakRelativeToBaseline(baseline: CalibrationBaseline | null, bs: Record<string, number>, emotion: string): boolean {
    if (!baseline) return false;

    const average = (keys: string[]) => avgKeys(bs, keys);
    const baselineRange = (group: keyof CalibrationBaseline): { p10: number; p90: number } | undefined => {
      const b = baseline[group];
      if (!b || typeof b === 'number') return undefined;
      return b as { p10: number; p90: number };
    };

    switch (emotion) {
      case 'happy': {
        const value = average(['mouthSmileLeft', 'mouthSmileRight']);
        const base = baselineRange('smile');
        return !!base && value <= base.p90 + 0.02;
      }
      case 'sad': {
        const frownValue = average(['mouthFrownLeft', 'mouthFrownRight']);
        const browDownValue = average(['browDownLeft', 'browDownRight']);
        const shrugLowerValue = average(['mouthShrugLower']);
        const frownBase = baselineRange('frown');
        const browBase = baselineRange('browDown');
        const shrugRange = baseline.auRanges?.mouthShrugLower;
        const shrugMedian = baseline.auMedians?.mouthShrugLower ?? 0;
        const shrugNeutralExcursion = shrugRange ? Math.max(0, shrugRange.p90 - shrugMedian) : 0;
        const shrugStrongSadThreshold = 0.25 + shrugNeutralExcursion * 0.40;
        if (shrugLowerValue > shrugStrongSadThreshold) return false;

        const frownSlack = frownBase ? Math.max(0.02, (frownBase.p90 - frownBase.p10) * 0.25) : 0.02;
        const browSlack = browBase ? Math.max(0.02, (browBase.p90 - browBase.p10) * 0.25) : 0.02;
        return !!frownBase && !!browBase && frownValue <= frownBase.p90 + frownSlack && browDownValue <= browBase.p90 + browSlack;
      }
      case 'angry': {
        const browValue = average(['browDownLeft', 'browDownRight']);
        const squintValue = average(['eyeSquintLeft', 'eyeSquintRight']);
        const browBase = baselineRange('browDown');
        const squintBase = baselineRange('eyeSquint');
        const browInRange = !!browBase && browValue <= browBase.p90 + 0.02;
        const squintInRange = !!squintBase && squintValue <= squintBase.p90 + 0.03;
        return browInRange && squintInRange;
      }
      case 'fear': {
        const value = average(['eyeWideLeft', 'eyeWideRight']);
        const base = baselineRange('eyeWide');
        return !!base && value <= base.p90 + 0.02;
      }
      case 'disgust': {
        const value = average(['noseSneerLeft', 'noseSneerRight']);
        const base = baselineRange('noseSneer');
        return !!base && value <= base.p90 + 0.02;
      }
      case 'surprise': {
        const eyeWide = average(['eyeWideLeft', 'eyeWideRight']);
        const eyeBase = baselineRange('eyeWide');
        return !!eyeBase && eyeWide <= eyeBase.p90 + 0.04;
      }
      case 'yawn': {
        const jawOpen = average(['jawOpen']);
        const jawBase = baselineRange('jawOpen');
        return !!jawBase && jawOpen <= jawBase.p90 + 0.06;
      }
      default:
        return false;
    }
  }

  scoreAllEmotions(
    baseline: CalibrationBaseline | null,
    bs: Record<string, number>,
    yawnDetected?: boolean,
    yawnScore?: number,
    eyeContext?: EyeContextSnapshot,
    detectorConfig?: EmotionDetectorConfig,
  ): EmotionScore[] {
    return scoreAllEmotionsWithScorer(baseline, bs, yawnDetected, yawnScore, eyeContext, detectorConfig);
  }

  computeStabilityFromSamples(samples: Record<string, number>[]): number {
    return computeBaselineStability(samples, STABILITY_KEYS);
  }

  computeStability(recentFrames: Record<string, number>[]): number {
    if (recentFrames.length < 2) return 1;
    let totalVariance = 0;
    for (const key of STABILITY_KEYS) {
      const values = recentFrames.map(f => f[key] ?? 0);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const variance = values.reduce((a, v) => a + (v - mean) ** 2, 0) / values.length;
      totalVariance += variance;
    }
    return totalVariance / STABILITY_KEYS.length;
  }

  duchenneFactor(bs: Record<string, number>): number {
    const cheek = avgKeys(bs, ['cheekSquintLeft', 'cheekSquintRight']);
    const eyeSquint = avgKeys(bs, ['eyeSquintLeft', 'eyeSquintRight']);
    const smile = avgKeys(bs, ['mouthSmileLeft', 'mouthSmileRight']);
    if (smile < 0.1) return 0;
    return clamp((cheek + eyeSquint) / (smile * 2), 0, 1);
  }

  isMouthOpen(bs: Record<string, number>): boolean {
    return avgKeys(bs, ['jawOpen']) > 0.28 || avgKeys(bs, ['mouthStretchLeft', 'mouthStretchRight']) > 0.20;
  }


  decideFocus(
    winner: EmotionScore,
    handNearFace: boolean,
    handNearMouth: boolean,
    yawnDetected: boolean,
    mouthOpen: boolean,
    stab: number,
    baselineStability: number,
    isPostYawn: boolean = false,
    blendshapes?: Record<string, number>,
  ): EmotionScore {
    // Yawn and focused are contradictory: hand near face right after a yawn
    // (covering mouth) is not a thinking pose. Block focused during cooldown.
    const handThinkingPose = (handNearFace || handNearMouth) && !yawnDetected && !mouthOpen && !isPostYawn;
    // baselineStability can be extremely small in clean calibration, so keep a practical floor.
    const stabilityLimit = Math.max(baselineStability * 3, 0.012);
    const stabOk = stab < 0 ? true : stab <= stabilityLimit;

    if (!handThinkingPose || !stabOk) {
      return winner;
    }

    // If there's a thinking pose, prefer focused over mild sad/happy classifications.
    if (winner.score >= 0.7 && winner.emotion !== 'neutral') {
      return winner; // respect strong emotion signal
    }

    // Hand is a boost, not the sole trigger. Require minimal facial evidence:
    // slight squint or lip tension to distinguish thinking from nose-scratching.
    const squint = blendshapes ? avgKeys(blendshapes, ['eyeSquintLeft', 'eyeSquintRight']) : 0;
    const lipTension = blendshapes ? Math.max(
      avgKeys(blendshapes, ['mouthPressLeft', 'mouthPressRight']),
      avgKeys(blendshapes, ['mouthRollLower']),
    ) : 0;
    const hasFacialEvidence = squint > 0.08 || lipTension > 0.08;

    if (!hasFacialEvidence) return winner;

    return {
      emotion: 'focused',
      score: 0.4,
      confidence: 0.85,
      valence: 0.1,
      arousal: 0.4,
    };
  }
}
